import {
    SharedMsalApplication
} from '@bayer-int/portletce-shared';
import type { RequestModifier ,
    CommonAuthorizerOptions,
    Predicate} from '@bayer-int/portletce-shared';

import type { Boot } from 'portlet/config/boot';

/**
 * Describes custom fetch behavior for particular data source.
 */
export interface AuthorizationIntegration {
    /** list of domains/predicates to which azure identity token should be sent as `Authorization: Bearer` */
    idTokenWhiteList(): CommonAuthorizerOptions['tokenDomainWhitelist'];
    /** list of domains/predicates to which azure access token should be sent as `Authorization: Bearer` */
    accessTokenWhiteList(): CommonAuthorizerOptions['accessTokenDomainWhitelist'];
    /** custom code allowing for modifying particular request */
    asModifier(): RequestModifier | null;
}

export type ApiIntegrationMethods = 'get' | 'post';

export class BWIntegration implements AuthorizationIntegration {
    private bwServersOrigins = [
        this.boot.servers.bwQueryService.H2D.url,
        this.boot.servers.bwQueryService.H2R.url,
        this.boot.servers.bwQueryService.H2S.url,
        this.boot.servers.bwQueryService.H2T.url,
    ].map(url => new URL(url).origin);

    private bwServersOriginPredicates: Array<Predicate<string>> = this.bwServersOrigins.map(
        origin => url =>
            url.toLowerCase().includes(origin.toLowerCase()) && url.includes('/oauth/'),
    );

    constructor(private boot: Boot) {}

    public idTokenWhiteList() {
        return this.bwServersOriginPredicates;
    }

    public accessTokenWhiteList() {
        return [];
    }

    public asModifier(): RequestModifier {
        return {
            test: url => this.bwServersOriginPredicates.some(predicate => predicate(url)),
            modifyRequest: (tokens, url, init = {}, opts) => {
                if (!tokens.idToken) return [url, init, opts];

                const headers = new Headers(init.headers);

                headers.delete('authorization');
                headers.set('bwauthorization', tokens.idToken);

                return [url, { ...init, headers: Object.fromEntries(headers.entries()) }, opts];
            },
            shouldForceRetry: res => {
                const hasTokenExpired = !res.ok && res.status === 401;

                return hasTokenExpired;
            },
        };
    }
}

export type APIGatewayOpts = {
    clientId: string;
    tentant: string;
    apiOrigin: string;
    subscriptionKey: string;
    apiScope: string;
    authorityBaseUrl?: string;
    redirectUri?: string;
    redirectUriForRedirectFlow?: string;
    allowedMethod?: ApiIntegrationMethods;
    mockObtainToken?: ((a: { scopes?: string[] }) => Promise<{ accessToken: string }>) | null;
};

export class APIGatewayIntegration implements AuthorizationIntegration {
    /**
     * required for fetching access token to the HR UNO API (API Gateway).
     * lazy loaded, so msal.js is loaded only when needed
     */
    private msalP = () =>
        SharedMsalApplication.get(
            this.opts.clientId,
            this.opts.authorityBaseUrl,
            this.opts.tentant,
            this.opts.redirectUri,
            this.opts.redirectUriForRedirectFlow,
            this.opts.apiScope,
        );

    constructor(private opts: Required<APIGatewayOpts>) {}

    public static configure({
        subscriptionKey,
        apiScope,
        clientId,
        tentant,
        apiOrigin,
        authorityBaseUrl = 'https://login.microsoftonline.com',
        redirectUri = location ? location.origin : '',
        redirectUriForRedirectFlow = '',
        allowedMethod = 'get',
        mockObtainToken = null,
    }: APIGatewayOpts): APIGatewayIntegration {
        return new APIGatewayIntegration({
            clientId,
            tentant,
            authorityBaseUrl,
            redirectUri,
            redirectUriForRedirectFlow,
            apiOrigin,
            subscriptionKey,
            apiScope,
            allowedMethod,
            mockObtainToken,
        });
    }

    public idTokenWhiteList() {
        return [];
    }
    public accessTokenWhiteList() {
        return [];
    }

    public asModifier(): RequestModifier {
        return {
            test: (url, init) => {
                const isUrlMatch = url.toLowerCase().startsWith(this.opts.apiOrigin.toLowerCase());

                if (init?.method) {
                    return isUrlMatch && init.method === this.opts.allowedMethod;
                }
                return isUrlMatch;
            },
            modifyRequest: (_tokens, url, init = {}, opts) => {
                /**
                 * We cannot use the _default_ accessToken, because it is not possible to generate
                 * access tokens for two different resources (graph api and HR UNO api).
                 * Instead, we have to request another token with appropriate scope.
                 */
                const request = { scopes: [this.opts.apiScope] };
                const obtainTokenP = this.opts.mockObtainToken
                    ? this.opts.mockObtainToken(request)
                    : this.msalP().then(msal => msal.obtainToken(request));

                return obtainTokenP.then(({ accessToken }) => {
                    const headers = new Headers(init.headers);

                    headers.set('authorization', `${accessToken}`);
                    headers.set('ocp-apim-subscription-key', this.opts.subscriptionKey);
                    return [url, { ...init, headers: Object.fromEntries(headers.entries()) }, opts];
                });
            },
            shouldForceRetry: () => false,
        };
    }
}

export type DatabricksProxyApiOpts = {
    clientId: string;
    tentant: string;
    databricksProxyApiBaseUrl: string;
    databricksProxyApiScope: string;
    authorityBaseUrl?: string;
    redirectUri?: string;
    redirectUriForRedirectFlow?: string;
    mockObtainToken?: ((a: { scopes?: string[] }) => Promise<{ accessToken: string }>) | null;
};

export class DatabricksProxyApiIntegration implements AuthorizationIntegration {
    private msalP = () =>
        SharedMsalApplication.get(
            this.opts.clientId,
            this.opts.authorityBaseUrl,
            this.opts.tentant,
            this.opts.redirectUri,
            this.opts.redirectUriForRedirectFlow,
            this.opts.databricksProxyApiScope,
        );

    constructor(private opts: Required<DatabricksProxyApiOpts>) {}

    public static configure({
        clientId,
        tentant,
        databricksProxyApiBaseUrl,
        databricksProxyApiScope,
        authorityBaseUrl = 'https://login.microsoftonline.com',
        redirectUri = location ? location.origin : '',
        redirectUriForRedirectFlow = '',
        mockObtainToken = null,
    }: DatabricksProxyApiOpts): DatabricksProxyApiIntegration {
        return new DatabricksProxyApiIntegration({
            clientId,
            tentant,
            authorityBaseUrl,
            redirectUri,
            redirectUriForRedirectFlow,
            databricksProxyApiBaseUrl,
            databricksProxyApiScope,
            mockObtainToken,
        });
    }

    public idTokenWhiteList() {
        return [];
    }
    public accessTokenWhiteList() {
        return [];
    }

    public asModifier(): RequestModifier {
        return {
            test: url => url.startsWith(this.opts.databricksProxyApiBaseUrl),
            modifyRequest: (_tokens, url, init = {}, opts) => {
                /**
                 * We cannot use the _default_ accessToken, because it is not possible to generate
                 * access tokens for two different resources (graph api and databricks proxy api).
                 * Instead, we have to request another token with appropriate scope.
                 */
                const request = { scopes: [this.opts.databricksProxyApiScope] };
                const obtainTokenP = this.opts.mockObtainToken
                    ? this.opts.mockObtainToken(request)
                    : this.msalP().then(msal => msal.obtainToken(request));

                return obtainTokenP.then(({ accessToken }) => {
                    const headers = new Headers(init.headers);

                    headers.set('authorization', `Bearer ${accessToken}`);

                    return [url, { ...init, headers: Object.fromEntries(headers.entries()) }, opts];
                });
            },
            shouldForceRetry: () => false,
        };
    }
}
